// Order confirmation page

import { setSEO, pageSEO } from '../seo';
import { formatPrice } from '../utils/formatting';
import { showConfetti } from '../ui/confetti';
import { showToast } from '../ui/toast';

export async function renderOrderConfirmation(app: HTMLElement, orderId: string) {
    setSEO(pageSEO.orderConfirmation(orderId));

    app.innerHTML = `
        <!-- Header -->
        <div class="navbar glass shadow-lg">
            <div class="navbar-start">
                <a href="/" class="btn btn-ghost btn-sm gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span class="hidden sm:inline">Back to Shop</span>
                </a>
            </div>
            <div class="navbar-center">
                <div class="font-bold text-lg md:text-xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Unilorin@50
                </div>
            </div>
            <div class="navbar-end"></div>
        </div>

        <div class="max-w-3xl mx-auto px-4 py-12 min-h-screen">
            <div class="card bg-base-100 shadow-2xl">
                <div class="card-body p-8 md:p-12">
                    <div class="flex flex-col items-center justify-center min-h-[400px]">
                        <span class="loading loading-spinner loading-lg text-primary mb-4"></span>
                        <p class="text-lg font-semibold">Verifying your payment...</p>
                        <p class="text-sm opacity-70 mt-2">Please wait a moment</p>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Get reference from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');

    if (!reference) {
        showError('Missing payment reference');
        return;
    }

    try {
        // Verify payment
        const verifyResponse = await fetch(`/api/payments/verify/${reference}`);
        const verifyData = await verifyResponse.json();

        if (!verifyData.success) {
            showError('Payment verification failed');
            return;
        }

        // Get order details
        const orderResponse = await fetch(`/api/orders/${orderId}`);
        const orderData = await orderResponse.json();

        if (orderData.success && orderData.data) {
            renderOrderDetails(orderData.data, verifyData.data.status);
            // Show confetti on successful payment
            if (verifyData.data.status === 'success') {
                setTimeout(() => showConfetti(), 300);
            }
        } else if (orderData.error?.code === 'ORDER_EXPIRED') {
            // Order link has expired (> 1 hour old)
            showToast('Order link has expired. Redirecting to homepage...', 'info');
            setTimeout(() => {
                window.location.href = '/';
            }, 2000);
        } else {
            showError('Failed to load order details');
        }
    } catch (error) {
        console.error('Error loading order:', error);
        showError('An error occurred while loading your order');
    }
}

function renderOrderDetails(order: any, paymentStatus: string) {
    const cardBody = document.querySelector('.card-body');
    if (!cardBody) return;

    const isSuccess = paymentStatus === 'success';

    cardBody.innerHTML = `
        <div class="text-center mb-8">
            <div class="inline-flex items-center justify-center w-24 h-24 rounded-full ${isSuccess ? 'bg-success/10' : 'bg-error/10'} mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 ${isSuccess ? 'text-success' : 'text-error'}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    ${isSuccess
                        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />'
                        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />'
                    }
                </svg>
            </div>
            <h1 class="text-3xl md:text-4xl font-bold mb-3">
                ${isSuccess ? 'Payment Successful!' : 'Payment Failed'}
            </h1>
            <p class="text-lg opacity-70">
                ${isSuccess ? 'Your order has been confirmed' : 'Your payment could not be processed'}
            </p>
        </div>

        ${isSuccess ? `
            <div class="space-y-6">
                <!-- Order Info Card -->
                <div class="bg-base-200 rounded-2xl p-6">
                    <h3 class="font-bold mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Order Details
                    </h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div class="text-xs opacity-60 mb-1">Order ID</div>
                            <div class="font-mono font-semibold">${order.id}</div>
                        </div>
                        <div>
                            <div class="text-xs opacity-60 mb-1">Customer</div>
                            <div class="font-semibold">${order.customer.name}</div>
                        </div>
                        <div>
                            <div class="text-xs opacity-60 mb-1">Email</div>
                            <div>${order.customer.email}</div>
                        </div>
                        <div>
                            <div class="text-xs opacity-60 mb-1">Phone</div>
                            <div>${order.customer.phone}</div>
                        </div>
                    </div>
                </div>

                <!-- Items -->
                <div>
                    <h3 class="font-bold mb-3">Order Items</h3>
                    <div class="space-y-2">
                        ${order.items.map((item: any) => `
                            <div class="flex justify-between items-center p-3 bg-base-200 rounded-lg">
                                <div>
                                    <div class="font-semibold">${item.product_name}</div>
                                    <div class="text-xs opacity-70">Quantity: ${item.quantity}</div>
                                </div>
                                <div class="font-bold text-primary">${formatPrice(item.subtotal)}</div>
                            </div>
                        `).join('')}
                    </div>
                </div>

                <!-- Payment Summary -->
                <div class="bg-base-200 rounded-2xl p-6">
                    <h3 class="font-bold mb-4">Payment Summary</h3>
                    <div class="space-y-3">
                        <div class="flex justify-between">
                            <span class="opacity-70">Subtotal</span>
                            <strong>${formatPrice(order.subtotal)}</strong>
                        </div>
                        <div class="flex justify-between text-sm opacity-70">
                            <span>Commitment Fee (50%)</span>
                            <span>${formatPrice(order.commitment_fee)}</span>
                        </div>
                        <div class="flex justify-between text-sm opacity-70">
                            <span>Transaction Fee</span>
                            <span>${formatPrice(order.transaction_fee)}</span>
                        </div>
                        <div class="divider my-2"></div>
                        <div class="flex justify-between text-success font-bold text-lg">
                            <span>Paid Now</span>
                            <strong>${formatPrice(order.amount_paid)}</strong>
                        </div>
                        <div class="flex justify-between text-warning font-semibold">
                            <span>Pay on Pickup</span>
                            <strong>${formatPrice(order.balance_due)}</strong>
                        </div>
                    </div>
                </div>

                ${order.free_bag ? `
                    <div class="alert alert-success">
                        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Your order includes a FREE branded packing bag!</span>
                    </div>
                ` : ''}

                <!-- Pickup Info -->
                <div class="alert alert-info">
                    <div>
                        <h4 class="font-bold mb-2 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Pickup Information
                        </h4>
                        <div class="space-y-1 text-sm">
                            <div>📅 ${order.pickup_date}</div>
                            <div>📍 Students' Union Building, University of Ilorin</div>
                            <div class="mt-2 opacity-90">⚠️ Please bring a valid ID and this order confirmation</div>
                        </div>
                    </div>
                </div>

                <div class="flex gap-3 justify-center">
                    <button class="btn btn-primary btn-lg" onclick="window.location.href='/'">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        Return to Shop
                    </button>
                </div>
            </div>
        ` : `
            <div class="text-center mt-6">
                <button class="btn btn-primary btn-lg" onclick="window.location.href='/'">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Try Again
                </button>
            </div>
        `}
    `;
}

function showError(message: string) {
    const cardBody = document.querySelector('.card-body');
    if (!cardBody) return;

    cardBody.innerHTML = `
        <div class="text-center">
            <div class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-error/10 mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
            <h1 class="text-3xl md:text-4xl font-bold mb-3">Error</h1>
            <p class="text-lg opacity-70 mb-8">${message}</p>
            <button class="btn btn-primary btn-lg" onclick="window.location.href='/'">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Return to Shop
            </button>
        </div>
    `;
}
